import { getCurrency } from '@common/utils/currency.ts'

import IconArrow from '@/assets/icons/icon-arrow-top.svg'
import { ChartColors } from '@/components/charts/utils.ts'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { formatMinutesAndHours, formatSeconds } from '@/utils/helpers.ts'

export interface TotalDataOption {
  name: string
  value: number
  backgroundColor?: string
}

export interface ChatterLegendOptions {
  legendData: {
    data: TotalDataOption[]
    total: string
    isCurrency?: boolean
    isMinutes?: boolean
    isSeconds?: boolean
  }
}

interface Props extends ChatterLegendOptions {
  title: string
  showCents?: boolean
  hideTotal?: boolean
}

export const TopChattersLegend = ({
  legendData,
  title,
  showCents,
  hideTotal,
}: Props) => {
  const { isCurrency, isMinutes, isSeconds } = legendData

  const mainChatter = legendData.data.slice(0, 7)
  const otherChatter = legendData.data.slice(7)
  const otherChatterTotal = Number(
    otherChatter
      .map((item) => item.value)
      .reduce((prev, curr) => prev + curr, 0)
      .toFixed(0),
  )

  return (
    <div className="flex w-60 flex-col gap-2">
      <div className="mb-3 flex w-full justify-between font-medium text-text-default">
        <p>{title}</p>
        {!hideTotal && <p>{legendData.total}</p>}
      </div>

      {mainChatter.slice(0, 30).map((item, index) => {
        const fixedValue = Number(item.value.toFixed(0))

        return (
          <div
            key={item.name}
            className="flex w-full justify-between text-sm font-medium text-text-default"
          >
            <div className="flex items-center gap-2">
              <div
                className="h-2 w-2 rounded-full"
                style={{
                  backgroundColor:
                    item.backgroundColor ||
                    ChartColors[index % ChartColors.length],
                }}
              />
              <p>{item.name}</p>
            </div>
            <p className="justify-end text-text-sub">
              {isCurrency
                ? getCurrency(fixedValue, showCents ? 2 : 0)
                : isMinutes
                  ? formatMinutesAndHours(fixedValue)
                  : isSeconds
                    ? formatSeconds(fixedValue)
                    : item.value.toFixed(0)}
            </p>
          </div>
        )
      })}
      {otherChatter.length > 0 && (
        <Tooltip
          side="bottom"
          content={otherChatter.slice(0, 30).map((item, index) => {
            const fixedValue = Number(item.value.toFixed(0))

            return (
              <div
                key={item.name}
                className="flex w-52 justify-between gap-2 text-sm font-medium"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="h-2 w-2 rounded-full"
                    style={{
                      backgroundColor:
                        item.backgroundColor ||
                        ChartColors[
                          (index + mainChatter.length) % ChartColors.length
                        ],
                    }}
                  />
                  <p className="truncate">{item.name}</p>
                </div>
                <p className="justify-end text-text-sub">
                  {isCurrency
                    ? getCurrency(fixedValue)
                    : isMinutes
                      ? formatMinutesAndHours(fixedValue)
                      : isSeconds
                        ? formatSeconds(fixedValue)
                        : fixedValue}
                </p>
              </div>
            )
          })}
        >
          <div className="flex w-full justify-between text-sm font-medium">
            <div className="flex items-center gap-2">
              <IconArrow className="h-2 w-2 rotate-180" />
              <p>Other accounts</p>
            </div>
            <p className="justify-end text-text-sub">
              {isCurrency
                ? getCurrency(otherChatterTotal)
                : isMinutes
                  ? formatMinutesAndHours(otherChatterTotal)
                  : isSeconds
                    ? formatSeconds(otherChatterTotal)
                    : otherChatterTotal}
            </p>
          </div>
        </Tooltip>
      )}
    </div>
  )
}
